class CatalogItem {

	constructor (catalog, data) {
		this.catalog	= catalog;
		this.code		= data.code;
		this.kind		= data.kind;
		this.label		= data.label;
		this.definition	= data.definition;
		this.superclass	= data.superclass;
		this.subclasses	= data.subclasses;
	}

	hasSuperclass () {
		return this.superclass !== null;
	}

	hasSubclasses () {
		return this.subclasses.length > 0;
	}

	isDiagnosis () {
		return this.subclasses.length === 0;
	}

	getTitle () {
		if (this.kind == 'chapter') {
			return `Kapitel: ${ this.code }`;
		}

		if (this.kind == 'grade' || this.kind == 'grade-specify' || this.kind == 'group') {
			return this.label;
		}

		//
		// Morphology Items with keys like 800:12 should be displayed as 800/12
		if (this.catalog.name == 'ICD-O-3') {
			return this.code.replace(':', '/');
		}

		return this.code;
	}

	getDescription () {
		if (this.kind == 'grade' || this.kind == 'grade-specify') {
			return this.definition;
		}

		if (this.kind == 'group') {
			return '';
		}

		if (this.catalog.name == 'OPS')
		{
			let levels		= this.getLevelsToDiagnosis().flat(3);
			let maxLevels	= Math.max(...levels);
			if (maxLevels < 3)
			{
				let upperItems	= this.getUpperItems(3 - maxLevels - 1);
				let prefix		= upperItems.reverse().map(item => item.label).join(': ');

				if (upperItems.length > 0)
					return `${ prefix }: ${ this.label }`;
			}

			return this.label;
		}

		return this.label;
	}

	getUpperItems (levels = 3, carry = []) {
		if (levels <= 0 || !this.superclass) {
			return carry;
		}

		carry.push(this.superclass);

		return this.superclass.getUpperItems(levels - 1, carry);
	}

	getLevelsToDiagnosis (carry = 0) {
		if (this.subclasses.length == 0) {
			return carry === 0 ? [0] : carry;
		}

		return this.subclasses.map(subclass => {
			return subclass.getLevelsToDiagnosis(carry + 1);
		});
	}

	generateSummary () {
		return `
			<div class="catalog-item" data-id="catalog-item" data-resolves-to="${ this.code }">
				<div class="catalog-item__icon flex-shrink-0">
					<i class="${ this.hasSubclasses() ? 'fal fa-folder' : 'fal fa-stethoscope' }"></i>
				</div>
				<div class="ml-2">
					<div class="catalog-item__title">${ this.getTitle() }</div>
					<div class="catalog-item__description">${ this.getDescription() }</div>
				</div>
			</div>
		`;
	}

	matchesQuery (query) {
		if (!this.isDiagnosis()) {
			return false;
		}

		return `${ this.prependSuperclassRecursively() } ${ this.code } ${ this.label }`.toLowerCase().includes(query.toLowerCase());
	}

	prependSuperclassRecursively ()
	{
		if (this.hasSuperclass())
		{
			const superclass = this.superclass;
			return `${ superclass.prependSuperclassRecursively() } ${ superclass.label }`;
		}

		return '';
	}
}

class Catalog {

	constructor (modal, data, commonlyUsed) {
		this.modal		= modal;

		this.name		= data.name;
		this.title		= data.title;
		this.version	= data.version;
		this.container	= {
			catalog:		this.modal.querySelector('[data-id="catalog"]'),
			commonlyUsed:	this.modal.querySelector('[data-id="commonly-used"]')
		};
		this.classes	= {};
		for (let code in data.classes) {
			this.classes[code] = new CatalogItem(this, data.classes[code]);
		}

		for (let code in this.classes) {
			const catalogClass = this.classes[code];

			if (catalogClass.superclass !== null) {
				this.classes[code].superclass = this.classes[catalogClass.superclass];
			}

			let subclasses = [];
			catalogClass.subclasses.forEach(subclass => {
				subclasses.push(this.classes[subclass]);
			});

			this.classes[code].subclasses = subclasses;
		}

		this.commonlyUsed = [];
		for (var key in commonlyUsed) {
			this.commonlyUsed.push({
				key:	key,
				count:	commonlyUsed[key]
			})
		}
		this.commonlyUsed.sort((a, b) => {
			if (a.count < b.count) return 1;
			if (a.count > b.count) return -1;
			return 0;
		})

		this.setCatalog(null);
		this.setCommonlyUsed(this.commonlyUsed.map(commonlyUsedKey => this.get(commonlyUsedKey.key)));

		//
		// Default to the catalog view
		this.switchToCommonlyUsed();

		//
		// Add listeners to switch between views
		this.modal.querySelector('[data-id="button-group-catalog"]').addEventListener('click', event => this.switchToCatalog(true));
		this.modal.querySelector('[data-id="button-group-commonly-used"]').addEventListener('click', event => this.switchToCommonlyUsed());

		/**
		 * Handles the click on item event
		 */
		this.modal.querySelectorAll('[data-id="items"]').forEach(node => node.addEventListener('click', event => {
			const catalogItem = event.target.closest('[data-id="catalog-item"]');
			if (catalogItem) {
				const resolvesTo = this.get(catalogItem.dataset.resolvesTo);

				//
				// Choose the clicked item if it is a diagnosis
				if (resolvesTo.isDiagnosis()) {
					this.selectItem(resolvesTo);
				} else {
					this.setCatalog(resolvesTo);
				}
			}
		}));

		/**
		 * Handles the back to previous section events
		 */
		this.modal.querySelector('[data-id="back-to-previous-section"]').addEventListener('click', event => {
			const previousSection = event.target.closest('[data-id="back-to-previous-section"]').dataset.resolvesTo;
			this.setCatalog(this.get(previousSection));
		});

		/**
		 * Handles the search events
		 */
		this.modal.querySelector('[data-id="catalog-search"]').addEventListener('input', event => {
			const searchString = event.target.value.trim();
			this.searchQuery(searchString);
		});
	}

	selectItem (resolvesTo, selectForIdPassed) {
		const selectForId = selectForIdPassed || this.modal.getAttribute('data-select-for');
		this.modal.removeAttribute('data-select-for');

		const summary		= document.querySelector(`[data-unique-diagnosis-id="${ selectForId }"]`);
		const icon			= summary.querySelector('[data-id="diagnosis-icon"]');
		const title			= summary.querySelector('[data-id="diagnosis-title"]');
		const description	= summary.querySelector('[data-id="diagnosis-description"]');
		const deleteIcon	= summary.querySelector('[data-id="icon-remove-item"]');

		icon.classList.remove('fa-plus');
		icon.classList.add('fa-stethoscope');

		title.innerHTML			= resolvesTo.getTitle();
		description.innerHTML	= resolvesTo.getDescription();
		deleteIcon.parentElement.classList.remove('d-none');

		const input = document.querySelector(`[data-diagnosis-field="${ selectForId }"]`);
		input.value = resolvesTo.code;
		$(input).trigger('input');

		$(this.modal).modal('hide');
	}

	rootClasses () {
		return Object.values(this.classes).filter(catalogClass => !catalogClass.hasSuperclass());
	}

	get (code) {
		return this.classes[code];
	}

	switchToCatalog (root = false) {

		if (root) {
			this.setCatalog();
		}

		const catalogButton			= this.modal.querySelector('[data-id="button-group-catalog"]');
		const commonlyUsedButton	= this.modal.querySelector('[data-id="button-group-commonly-used"]');

		catalogButton.classList.add('btn-group-button--active');
		commonlyUsedButton.classList.remove('btn-group-button--active');

		this.container.catalog.classList.remove('d-none');
		this.container.commonlyUsed.classList.add('d-none');
	}

	switchToCommonlyUsed () {
		const catalogButton			= this.modal.querySelector('[data-id="button-group-catalog"]');
		const commonlyUsedButton	= this.modal.querySelector('[data-id="button-group-commonly-used"]');

		catalogButton.classList.remove('btn-group-button--active');
		commonlyUsedButton.classList.add('btn-group-button--active');

		this.container.catalog.classList.add('d-none');
		this.container.commonlyUsed.classList.remove('d-none');

		this.setSearchResults([]);
	}

	searchQuery (query = null) {
		this.switchToCatalog();

		if (query === null || query.length == 0) {
			this.container.catalog.querySelector('[data-id="regular-picker"]').classList.remove('d-none');
			this.container.catalog.querySelector('[data-id="search-results"]').classList.add('d-none');

			this.setSearchResults([]);
		} else {
			const classes = Object.values(this.classes).filter(item => item.matchesQuery(query)).slice(0, 100);
			this.setSearchResults(classes);

			this.container.catalog
				.querySelector('[data-id="search-results"] [data-id="empty-search-results"]')
				.classList[classes.length == 0 ? 'remove' : 'add']('d-none');

			this.container.catalog.querySelector('[data-id="regular-picker"]').classList.add('d-none');
			this.container.catalog.querySelector('[data-id="search-results"]').classList.remove('d-none');
		}
	}

	setCommonlyUsed (classes = []) {
		const container = this.container.commonlyUsed;
		container.querySelector('[data-id="items"]').innerHTML = classes.map(item => item.generateSummary()).join('');
	}

	setSearchResults (classes = []) {
		const container = this.container.catalog.querySelector('[data-id="search-results"]');
		container.querySelector('[data-id="items"]').innerHTML = classes.map(item => item.generateSummary()).join('');
	}

	setCatalog (rootItem = null) {
		const container 	= this.container.catalog;
		const classes		= rootItem === null ? this.rootClasses() : rootItem.subclasses;
		const title			= rootItem === null ? `${ this.name } Katalog (${ this.version })` : rootItem.getTitle();
		const description	= rootItem === null ? `` : rootItem.getDescription();
		const backTitle		= rootItem === null ? null : (rootItem.hasSuperclass() ? rootItem.superclass.getTitle() : `${ this.name } Katalog (${ this.version})`);

		const previousSection = rootItem === null || !rootItem.hasSuperclass() ? null : rootItem.superclass.code;
		container.querySelector('[data-id="back-to-previous-section"]').setAttribute('data-resolves-to', previousSection);

		container.querySelector('[data-id="back-to-previous-section"]').innerHTML		= backTitle === null ? null : `<i class="mr-2 fal fa-angle-left"></i>${ backTitle }`;
		container.querySelector('[data-id="visible-section-title"]').innerText			= title;
		container.querySelector('[data-id="visible-section-description"]').innerText	= description;

		container.querySelector('[data-id="items"]').innerHTML = classes.map(item => item.generateSummary()).join('');
	}
}

class CatalogRegistry {

	constructor () {
		this.catalogs	= [];

		const classificationCatalogs = document.querySelector('#classification-catalogs');
		if (classificationCatalogs) {

			//
			// Lazy load the catalogs for each revision
			const modals = classificationCatalogs.querySelectorAll('[data-fqcn][data-revision]');
			this.register(modals);
		}

		document.addEventListener('click', event => {
			const catalogTrigger = event.target.closest('[opens-catalog]');
			if (!catalogTrigger) {
				return;
			}

			const catalogModal		= document.querySelector(catalogTrigger.dataset.opensModal);

			const clickedDeleteNode = event.target.closest('[data-id="icon-remove-item"]');
			if (clickedDeleteNode) {
				this.resetItem(catalogTrigger);
			} else {
				this.catalogs.forEach(catalog => {
					if (catalog.modal.isSameNode(catalogModal)) {
						catalog.setCatalog(null);
					}
				});

				catalogModal.setAttribute('data-select-for', catalogTrigger.dataset.uniqueDiagnosisId);
				catalogModal.setAttribute('data-dismisses-to', catalogTrigger.dataset.dismissesModal);

				$(catalogModal).modal('show');
				$(catalogTrigger.dataset.dismissesModal).modal('hide');
			}
		});
	}

	resetItem (catalogTrigger) {
		const iconNode = catalogTrigger.querySelector('[data-id="diagnosis-icon"]');
		iconNode.classList.remove('fa-stethoscope');
		iconNode.classList.add('fa-plus');

		const titleNode				= catalogTrigger.querySelector('[data-id="diagnosis-title"]');
		titleNode.innerText			= titleNode.dataset.emptyText;

		const descriptionNode		= catalogTrigger.querySelector('[data-id="diagnosis-description"]');
		descriptionNode.innerText	= descriptionNode.dataset.emptyText;

		const uniqueId				= catalogTrigger.dataset.uniqueDiagnosisId;
		const input					= document.querySelector(`[data-diagnosis-field="${ uniqueId }"]`);
		input.value					= null;
		$(input).trigger('input');

		catalogTrigger.querySelector('[data-id="icon-remove-item"]').parentElement.classList.add('d-none');
	}

	setItem (modalId, item, selectForId) {
		return this.getCatalog(modalId).selectItem(item, selectForId);
	}

	getItem (modalId, item) {
		return this.getCatalog(modalId).get(item);
	}

	getCatalog (modalId) {
		return this.catalogs.filter(catalog => catalog.modal.getAttribute('id') === modalId)[0];
	}

	register (modals) {

		let payload = [];
		modals.forEach(modal => {
			const fqcn		= modal.dataset.fqcn;
			const revision	= modal.dataset.revision;
			const root		= modal.dataset.root;

			const uuid 		= window.duo.uuid();
			modal.setAttribute('catalog-identifier', uuid);

			$(modal).on('hide.bs.modal', event => {
				const dismissesTo = modal.dataset.dismissesTo;
				$(dismissesTo).modal('show');
			});

			payload.push({ uuid, fqcn, revision, root });
		});

		$.post({
			url: route('post-catalog'),
			data: { payload },
			success: response => {
				response.forEach(data => {

					//
					// Update the modals subheading
					const classificationCatalogs = document.querySelector('#classification-catalogs');
					const modal = classificationCatalogs.querySelector(`[data-fqcn][data-revision][catalog-identifier="${ data.uuid }"]`);
					this.catalogs.push(new Catalog(modal, data.catalog, data.commonlyUsed));
				})
			},
			complete: () => {
				duo.table.manualRebuild();
			}
		});
	}

}

export default CatalogRegistry;
